import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { EventTemplateCardData } from '@models/design-templates/event-template-card-data.model';
import { EventAttendee } from '@models/event-attendance/event-attendee.model';
import { RSVPOptionType } from '@models/event-attendance/rsvp-option-type.enum';
import { CreateOrUpdateEventConfirmationPageCardRequest } from '@models/events/dto/create-or-update-event-confirmation-page-card.request';
import { EventConfirmationPageCardType } from '@models/events/enums/event-confirmation-page-card-type.enum';
import { EventConfirmationPageCard } from '@models/events/event-confirmation-page-card.enum';
import { EventRSVPConfirmationPageSectionType } from '@models/events/event-rsvp-confirmation-page-section-type.enum';
import { EventSettings } from '@models/events/event-settings.enum';
import { Event } from '@models/events/event.model';
import { OneTimePricingPlan } from '@models/pricing-plans/one-time-pricing-plan.model';
import { User } from '@models/users/user.model';
import { YesNoDialog } from '@modules/customer/dialogs/yes-no/yes-no.dialog';
import { EditSocialMediaButtonsComponent } from '@modules/events/components/edit-social-media-buttons/edit-social-media-buttons.component';
import { DesignTemplateCardComponent } from '@modules/shared/components/design-template-card/design-template-card.component';
import { RichTextEditorComponent } from '@modules/shared/components/rich-text-editor/rich-text-editor.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services/auth/auth.service';
import { EventService } from '@services/events/event.service';
import { ScreenWidthService } from '@services/shared/screen-width.service';
import { SidebarService } from '@services/sidebar.service';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { debounceTime, Subject } from 'rxjs';
import { SwiperOptions } from 'swiper/types';
import { SwiperDirective } from '../../../../../../directives/swiper.directive';
import { SafeHtmlPipe } from '../../../../../../pipes/safe-html.pipe';
import { AppRoutes } from '../../../../../../routes';

@Component({
  selector: 'app-generic-after-attend-section-card',
  standalone: true,
  imports: [
    SafeHtmlPipe,
    EditSocialMediaButtonsComponent,
    TranslateModule,
    DesignTemplateCardComponent,
    SwiperDirective,
    NgxTippyModule,
    RichTextEditorComponent,
  ],
  templateUrl: './generic-after-attend-section-card.component.html',
  styleUrl: './generic-after-attend-section-card.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class GenericAfterAttendSectionCardComponent implements OnInit {
  @Input() eventConfirmationPage?: EventConfirmationPageCard;
  @Input() rsvpType?: RSVPOptionType;
  @Input() attendee?: EventAttendee;
  @Input() withTickets?: boolean = false;
  @Input() withSlots?: boolean = false;
  @Input() isEdit?: boolean = false;
  @Input() event?: Event;
  @Input() designTemplates: EventTemplateCardData[] = [];
  @Input() oneTimePricingPlans?: OneTimePricingPlan[];
  @Output() onClose = new EventEmitter();
  loggedUser?: User;
  isHidden?: boolean = false;
  isDesktop?: boolean = true;
  private _type?: EventConfirmationPageCardType;
  private titleSubject = new Subject<string>();
  private descriptionSubject = new Subject<string>();
  showSocialMediaLinks: boolean = false;
  @Input()
  set type(value: EventConfirmationPageCardType | undefined) {
    this._type = value;
  }

  get type(): EventConfirmationPageCardType | undefined {
    return this._type;
  }

  swiperConfig: SwiperOptions = {
    slidesPerView: 'auto',
    speed: 2000,
    spaceBetween: 8,
    autoplay: true,

    loop: true,
    injectStyles: [
      `
        :host .swiper { overflow: visible;}
        :host .swiper-wrapper { justify-content: center; }
        :host .slider-active .swiper-wrapper { justify-content: normal; }
      `,
    ],
  };

  constructor(
    private router: Router,
    private translateService: TranslateService,
    private authService: AuthService,
    private viewContainerRef: ViewContainerRef,
    private dialog: MatDialog,
    private screenWidthService: ScreenWidthService,
    private eventService: EventService,
    private sidebarService: SidebarService,
  ) {
    this.screenWidthService
      .isDesktop()
      .pipe(takeUntilDestroyed())
      .subscribe((isDesktop) => (this.isDesktop = isDesktop));

    this.authService.userSubject
      .pipe(takeUntilDestroyed())
      .subscribe((user) => (this.loggedUser = user));
  }

  ngOnInit(): void {
    if (this.type === EventConfirmationPageCardType.SOCIAL_MEDIA) {
      this.showSocialMediaLinks = this.haveSocialMediaLinks();
    }

    this.titleSubject.pipe(debounceTime(500)).subscribe((value) => {
      this.performSave('title', value);
    });

    this.descriptionSubject.pipe(debounceTime(500)).subscribe((value) => {
      this.performSave('description', value);
    });

    const card = this.findCardByType();
    if (card) {
      this.isHidden = card.hidden;
    }
  }

  private performSave(type: string, content: string) {
    if (!this.event) return;

    if (this.type) {
      const request: CreateOrUpdateEventConfirmationPageCardRequest = {
        type: this.type,
        [type]: content,
      };

      this.eventService
        .editConfirmationPageCard(this.event.id, request)
        .subscribe({
          next: (res) => {
            this.eventConfirmationPage = res;
          },
        });
    }
  }

  isCardEditable() {
    if (!this.type) return;
    const editableCardTypes = [EventConfirmationPageCardType.SOCIAL_MEDIA];
    return editableCardTypes.includes(this.type);
  }

  editSocialMedia(event: any) {
    event.stopPropagation();
    if (this.isEdit) {
      localStorage.setItem(
        'activeItem',
        EventSettings.getDisplayName(EventSettings.SOCIAL_MEDIA),
      );
      this.sidebarService.toggleSidebar();
    }
  }

  saveEditorContent(valueObj: any, type: string) {
    if (type === 'title') {
      this.titleSubject.next(valueObj.value);
    } else if (type === 'description') {
      this.descriptionSubject.next(valueObj.value);
    }
  }
  getIcon() {
    switch (this.type) {
      case EventConfirmationPageCardType.CONFIRMATION: {
        if (this.withTickets || this.withSlots) {
          return '<i class="fa-regular fa-circle-check"></i>';
        }
        switch (this.rsvpType) {
          case RSVPOptionType.NO: {
            return '<i class="fa-regular fa-circle-xmark"></i>';
          }
          case RSVPOptionType.PENDING_APPROVAL: {
            return '<i class="fa-regular fa-hourglass-half"></i>';
          }
          case RSVPOptionType.WAITLIST: {
            return '<i class="fa-regular fa-clipboard-list"></i>';
          }
          case RSVPOptionType.WITHDRAW: {
            return '<i class="fa-regular fa-circle-xmark"></i>';
          }
          case RSVPOptionType.MAYBE: {
            return '<i class="fa-regular fa-circle-xmark"></i>';
          }
          default:
            return '<i class="fa-regular fa-circle-check"></i>';
        }
      }
      case EventConfirmationPageCardType.CREATE_ACCOUNT: {
        return '<i class="fa-regular fa-id-card-clip"></i>';
      }
      case EventConfirmationPageCardType.SOCIAL_MEDIA: {
        return '<i class="fa-regular fa-users"></i>';
      }
      case EventConfirmationPageCardType.CREATE_EVENT: {
        return '<i class="fa-regular fa-palette"></i>';
      }
      default:
        return '';
    }
  }

  toggleVisible() {
    this.isHidden = !this.isHidden;
    if (!this.event || !this.type) return;
    this.eventService
      .editConfirmationPageCard(this.event.id, {
        type: this.type,
        hidden: this.isHidden,
      })
      .subscribe({
        next: (res) => {},
      });
  }

  updateToProDialog() {
    const dialogRef = this.dialog.open(YesNoDialog, {
      maxWidth: '602px',
      maxHeight: '100vh',
      width: '100%',
      height: 'auto',
      data: {
        message: 'APP.PRO_FEATURES.PRO_FOR_EDIT',
        yesLabel: 'APP.PRO_FEATURES.UPGRADE_TO_PRO',
        noLabel: 'APP.PRO_FEATURES.UNDERSTOOD',
        isPopUpActions: true,
      },
      panelClass: ['normal-dialog'],
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.router.navigate(['/', AppRoutes.Customer.settings], {
          queryParams: {
            tab: 'plan',
          },
        });
      }
    });
  }

  isProSection() {
    if (!this.type) return;

    const proSectionsType = [
      EventConfirmationPageCardType.CONFIRMATION,
      EventConfirmationPageCardType.CREATE_ACCOUNT,
      EventConfirmationPageCardType.CREATE_EVENT,
    ];
    return proSectionsType.includes(this.type);
  }
  goToSignUp() {
    this.router.navigate(['/', AppRoutes.Root.signUp], {
      queryParams: {
        email: this.attendee?.getEmail(),
      },
    });
    this.onClose.emit();
  }

  createEvent() {
    this.router.navigate(['/', AppRoutes.Events.createEvent]);
    this.onClose.emit();
  }

  getTitle() {
    const card = this.findCardByType();
    if (card && card.title) return card.title;

    let message = '';
    switch (this.type) {
      case EventConfirmationPageCardType.CONFIRMATION: {
        switch (this.rsvpType) {
          case RSVPOptionType.YES: {
            message = this.translateService.instant(
              'APP.EVENT_AFTER_ATTEND.GENERIC.PARTICIPATION_CONFIRMED',
            );
            break;
          }
          case RSVPOptionType.NO: {
            message = this.translateService.instant(
              'APP.EVENT_AFTER_ATTEND.GENERIC.PARTICIPATION_DECLINED',
            );
            break;
          }
          case RSVPOptionType.MAYBE: {
            message = this.translateService.instant(
              'APP.EVENT_AFTER_ATTEND.GENERIC.PARTICIPATION_DECLINED',
            );
            break;
          }
          case RSVPOptionType.WAITLIST: {
            message = this.translateService.instant(
              'APP.EVENT_AFTER_ATTEND.GENERIC.WAIT_LIST_CONFIRMED',
            );
            break;
          }
          case RSVPOptionType.WITHDRAW: {
            message = this.translateService.instant(
              'APP.EVENT_AFTER_ATTEND.GENERIC.PARTICIPATION_WITHDRAWN',
            );
            break;
          }
          case RSVPOptionType.PENDING_APPROVAL: {
            message = this.translateService.instant(
              'APP.EVENT_AFTER_ATTEND.GENERIC.APPROVAL_PENDING',
            );
            break;
          }
        }
        if (this.withTickets && this.withSlots) {
          message = this.translateService.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.SLOTS_BOOKING_SUCCESSFULLY',
          );
        }
        return message;
      }
      case EventConfirmationPageCardType.CREATE_ACCOUNT: {
        return this.translateService.instant(
          'APP.EVENT_AFTER_ATTEND.GENERIC.CREATE_ACCOUNT_FOR_BETTER_EXPERIENCE',
        );
      }
      case EventConfirmationPageCardType.SOCIAL_MEDIA: {
        return this.translateService.instant(
          'APP.EVENT_AFTER_ATTEND.GENERIC.LETS_CONNECT',
        );
      }
      case EventConfirmationPageCardType.CREATE_EVENT: {
        return this.translateService.instant(
          'APP.EVENT_AFTER_ATTEND.GENERIC.CREATE_YOUR_OWN_EVENT',
        );
      }
      default:
        return '';
    }
  }

  getDescription() {
    let confirmationMessage = '';

    const card = this.findCardByType();
    if (card && card.description) return card.description;

    switch (this.type) {
      case EventConfirmationPageCardType.CONFIRMATION: {
        if (
          this.attendee?.getEmail() &&
          this.attendee?.getEmail() !== '-' &&
          this.attendee?.getEmail().trim() !== ''
        ) {
          confirmationMessage = `${this.translateService.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.CONFIRMATION_SENT',
          )} ${this.attendee?.getEmail()}`;
          if (this.withTickets) {
            confirmationMessage = `${this.translateService.instant(
              'APP.EVENT_AFTER_ATTEND.GENERIC.CONFIRMATION_AND_TICKETS_SENT',
            )} ${this.attendee?.getEmail()}`;
          }
        } else if (
          this.attendee?.getPhone() &&
          this.attendee?.getPhone() !== '-' &&
          this.attendee?.getPhone().trim() !== ''
        ) {
          confirmationMessage = `${this.translateService.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.CONFIRMATION_SENT_PHONE',
          )} ${this.attendee?.getPhone()}`;
        } else {
          confirmationMessage = `${this.translateService.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.CONFIRMATION_SENT',
          )}`;
        }
        return confirmationMessage;
      }
      case EventConfirmationPageCardType.CREATE_ACCOUNT: {
        return this.translateService.instant(
          'APP.EVENT_AFTER_ATTEND.GENERIC.MANAGE_YOUR_TICKETS',
        );
      }
      case EventConfirmationPageCardType.SOCIAL_MEDIA: {
        return this.translateService.instant(
          'APP.EVENT_AFTER_ATTEND.GENERIC.CHECK_OUR_SOCIAL_MEDIA',
        );
      }
      case EventConfirmationPageCardType.CREATE_EVENT: {
        return this.translateService.instant(
          'APP.EVENT_AFTER_ATTEND.GENERIC.CHOSE_TEMPLATES_OR_USE_AI',
        );
      }
      default:
        return '';
    }
  }

  findCardByType() {
    if (!this.event) return;

    return this.event.confirmationPageCards?.find((x) => x.type === this.type);
  }

  haveSocialMediaLinks() {
    if (
      !this.event ||
      !this.event.eventTemplate ||
      !this.event.eventTemplate.socialMedia
    )
      return false;

    return this.event.eventTemplate.socialMedia.links.length > 0;
  }

  protected readonly EventRSVPConfirmationPageSectionType =
    EventRSVPConfirmationPageSectionType;
  protected readonly EventConfirmationPageCardType =
    EventConfirmationPageCardType;
}
